import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, DateRangePicker } from '../components/common';
import { ROUTES } from '../constants';
import { marketerService, ApiError } from '../services';

const MarketersPage = () => {
  const navigate = useNavigate();
  const hasInitialLoadRef = useRef(false);

  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  // State for API data
  const [marketers, setMarketers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [exportResult, setExportResult] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    size: 20,
    count: 0,
    total: 0,
  });

  // State for filtering
  const [filters, setFilters] = useState({
    searchQuery: '',
    dateFrom: '',
    dateTo: '',
  });

  // Fetch marketers data from API
  const fetchMarketers = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);

    try {
      const requestParams = {
        page: params.page || 1,
        size: params.size || 20,
        order: params.order || 'created_at ASC',
      };

      // Add keyword if provided
      if (params.keyword) {
        requestParams.keyword = params.keyword;
      }

      // Add date range if provided
      if (params.dateFrom && params.dateTo) {
        requestParams.dateFrom = params.dateFrom;
        requestParams.dateTo = params.dateTo;
      }

      const response = await marketerService.getFormattedMarketerList(requestParams);

      if (response && response.data) {
        setMarketers(response.data.data || []);
        setPagination({
          page: response.data.page || 1,
          pageCount: response.data.pageCount || 1,
          size: response.data.size || 20,
          count: response.data.count || 0,
          total: response.data.total || 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch marketers:', err);

      if (err instanceof ApiError) {
        setError(`Failed to load marketers: ${err.message}`);
      } else {
        setError('Failed to load marketers. Please try again.');
      }

      // Set empty data on error
      setMarketers([]);
      setPagination(prev => ({ ...prev, count: 0, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, []); // Remove pagination dependencies to prevent recreation

  // Search marketers (if search API is available)
  const searchCreators = useCallback(
    async query => {
      if (!query || query.length < 3) return;

      setLoading(true);
      setError(null);

      try {
        const response = await marketerService.searchMarketers({
          query,
          page: 1,
          size: pagination.size,
        });

        if (response && response.data) {
          setMarketers(response.data.data || []);
          setPagination(prev => ({
            ...prev,
            page: response.data.page || 1,
            pageCount: response.data.pageCount || 1,
            count: response.data.count || 0,
            total: response.data.total || 0,
          }));
        }
      } catch (err) {
        console.error('Failed to search marketers:', err);

        if (err instanceof ApiError && err.status === 404) {
          // Search endpoint might not be implemented yet, fall back to client-side filtering
          console.warn('Search API not available, using client-side filtering');
          // Use direct service call instead of fetchCreators to avoid circular dependency
          try {
            const fallbackResponse = await marketerService.getFormattedMarketerList({ page: 1, size: 50 });
            if (fallbackResponse && fallbackResponse.data) {
              setMarketers(fallbackResponse.data.data || []);
              setPagination({
                page: fallbackResponse.data.page || 1,
                pageCount: fallbackResponse.data.pageCount || 1,
                size: fallbackResponse.data.size || 50,
                count: fallbackResponse.data.count || 0,
                total: fallbackResponse.data.total || 0,
              });
            }
          } catch (fallbackErr) {
            console.error('Failed to fetch fallback data:', fallbackErr);
            setError('Failed to search marketers. Please try again.');
          }
        } else {
          setError('Failed to search marketers. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    },
    [pagination.size]
  );

  // Handle search input with minimum 3 characters
  const handleSearchFocus = useCallback(() => {
    setSearchFocused(true);
    if (searchTerm.length >= 3) {
      setShowSearchResults(true);
    }
  }, [searchTerm]);

  const handleSearchChange = useCallback(e => {
    const value = e.target.value;
    setSearchTerm(value);

    if (value.length >= 3) {
      setShowSearchResults(true);
      setFilters(prev => ({ ...prev, searchQuery: value }));
    } else {
      setShowSearchResults(false);
      setFilters(prev => ({ ...prev, searchQuery: '' }));
    }
  }, []);

  const handleSearchBlur = useCallback(() => {
    setSearchFocused(false);
    // Delay hiding results to allow for clicks
    setTimeout(() => setShowSearchResults(false), 200);
  }, []);

  // Handle date range filtering
  const handleDateRangeChange = useCallback((startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: startDate,
      dateTo: endDate,
    }));
  }, []);

  // Apply filters
  const applyFilters = useCallback(async () => {
    const hasSearchQuery =
      filters.searchQuery && filters.searchQuery.length >= 3;
    const hasDateRange = filters.dateFrom && filters.dateTo;

    const requestParams = {
      page: 1,
      size: pagination.size
    };

    // Add keyword if search query exists
    if (hasSearchQuery) {
      requestParams.keyword = filters.searchQuery;
    }

    // Add date range if both dates exist
    if (hasDateRange) {
      requestParams.dateFrom = filters.dateFrom;
      requestParams.dateTo = filters.dateTo;
    }

    // Always use the same endpoint with appropriate parameters
    await fetchMarketers(requestParams);
  }, [
    filters.searchQuery,
    filters.dateFrom,
    filters.dateTo,
    fetchMarketers,
    pagination.size,
  ]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setShowSearchResults(false);
    setFilters({
      searchQuery: '',
      dateFrom: '',
      dateTo: '',
    });
    fetchMarketers({
      page: 1,
      size: pagination.size
    });
  }, [fetchMarketers, pagination.size]);

  // Handle pagination
  const handlePageChange = useCallback(
    newPage => {
      const hasSearchQuery =
        filters.searchQuery && filters.searchQuery.length >= 3;
      const hasDateRange = filters.dateFrom && filters.dateTo;

      const requestParams = {
        page: newPage,
        size: pagination.size
      };

      // Maintain keyword search when paginating
      if (hasSearchQuery) {
        requestParams.keyword = filters.searchQuery;
      }

      // Maintain date range when paginating
      if (hasDateRange) {
        requestParams.dateFrom = filters.dateFrom;
        requestParams.dateTo = filters.dateTo;
      }

      fetchMarketers(requestParams);
    },
    [
      fetchMarketers,
      filters.searchQuery,
      filters.dateFrom,
      filters.dateTo,
      pagination.size,
    ]
  );

  // Handle view marketer details
  const handleViewDetails = useCallback((marketer) => {
    navigate(`/marketers/${marketer.id}`, {
      state: { marketer }
    });
  }, [navigate]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setError(null);
    setExportResult(null);

    try {
      const requestParams = {};

      // Use current filters for export
      const hasSearchQuery = filters.searchQuery && filters.searchQuery.length >= 3;
      const hasDateRange = filters.dateFrom && filters.dateTo;

      if (hasSearchQuery) {
        requestParams.keyword = filters.searchQuery;
      }

      if (hasDateRange) {
        requestParams.dateFrom = filters.dateFrom;
        requestParams.dateTo = filters.dateTo;
      }

      const response = await marketerService.exportMarketerList(requestParams);

      // Backend now returns JSON instead of CSV file
      // Display the export information
      if (response.data) {
        // setExportResult(response.data?.message);
        alert(response.data?.message);
      }
    } catch (err) {
      console.error('Failed to export creators:', err);
      if (err instanceof ApiError) {
        setError(`Failed to export creators: ${err.message}`);
      } else {
        setError('Failed to export creators. Please try again.');
      }
    } finally {
      setExporting(false);
    }
  }, [filters.searchQuery, filters.dateFrom, filters.dateTo]);

  // Initial data fetch - using ref to prevent double calls
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      fetchMarketers({ page: 1, size: 20, order: 'created_at DESC' });
    }
  }, []); // Empty dependency array to only run once

  // Auto-trigger date range filtering when both dates are selected
  useEffect(() => {
    if (startDate && endDate) {
      handleDateRangeChange(startDate, endDate);
    }
  }, [startDate, endDate, handleDateRangeChange]);

  return (
    <div>
      <PageHeader
        title='Creators Management'
        subtitle='Manage your creator team and their activities'
        breadcrumbs={[
          { name: 'Home', href: ROUTES.DASHBOARD },
          { name: 'Creators' },
        ]}
      />

      {/* Quick Stats */}
      {/*
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
        <div className='bg-white p-4 rounded-lg shadow-sm border border-gray-200'>
          <div className='flex items-center'>
            <div className='w-8 h-8 bg-green-100 rounded-md flex items-center justify-center'>
              <svg
                className='w-5 h-5 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <p className='text-sm font-medium text-gray-600'>Active</p>
              <p className='text-lg font-semibold text-gray-900'>89</p>
            </div>
          </div>
        </div>

        <div className='bg-white p-4 rounded-lg shadow-sm border border-gray-200'>
          <div className='flex items-center'>
            <div className='w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center'>
              <svg
                className='w-5 h-5 text-yellow-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <p className='text-sm font-medium text-gray-600'>Pending</p>
              <p className='text-lg font-semibold text-gray-900'>12</p>
            </div>
          </div>
        </div>

        <div className='bg-white p-4 rounded-lg shadow-sm border border-gray-200'>
          <div className='flex items-center'>
            <div className='w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center'>
              <svg
                className='w-5 h-5 text-blue-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <p className='text-sm font-medium text-gray-600'>Performance</p>
              <p className='text-lg font-semibold text-gray-900'>92%</p>
            </div>
          </div>
        </div>

        <div className='bg-white p-4 rounded-lg shadow-sm border border-gray-200'>
          <div className='flex items-center'>
            <div className='w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center'>
              <svg
                className='w-5 h-5 text-purple-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1'
                />
              </svg>
            </div>
            <div className='ml-3'>
              <p className='text-sm font-medium text-gray-600'>Revenue</p>
              <p className='text-lg font-semibold text-gray-900'>$45k</p>
            </div>
          </div>
        </div>
      </div>
      */}

      {/* Search and Filter Section */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6'>
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {/* Search Bar */}
          <div className='relative'>
            <label
              htmlFor='search'
              className='block text-sm font-medium text-gray-700 mb-2'
            >
              Search Creators
            </label>
            <div className='relative'>
              <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                <svg
                  className='h-5 w-5 text-gray-400'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <input
                type='text'
                id='search'
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                placeholder='Search by name or email (min 3 chars)...'
                className={`
                  block w-full pl-10 pr-3 py-2 border rounded-md text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                  ${searchFocused ? 'border-blue-300' : 'border-gray-300'}
                  ${searchTerm.length > 0 && searchTerm.length < 3
                    ? 'border-yellow-300 bg-yellow-50'
                    : ''
                  }
                `}
              />
              {searchTerm.length > 0 && searchTerm.length < 3 && (
                <div className='absolute right-3 top-2'>
                  <div className='bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded'>
                    {3 - searchTerm.length} more
                  </div>
                </div>
              )}
            </div>
          </div >

          {/* Join Date Range Filter */}
          < DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onDateRangeChange={({ startDate: start, endDate: end }) => {
              handleDateRangeChange(start, end);
            }}
            placeholder='Select join date range'
            label={{
              start: 'Join Date From',
              end: 'Join Date To',
            }}
          />

          {/* Filter Actions */}
          <div className='flex flex-col justify-end md:col-span-2 lg:col-span-1'>
            <div className='flex flex-col sm:flex-row gap-2'>
              <button
                onClick={clearFilters}
                className='flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500'
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                disabled={loading}
                className='flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {loading ? 'Applying...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </div >
      </div >

      {/* Error Display */}
      {
        error && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6'>
            <div className='flex items-center'>
              <svg
                className='w-5 h-5 text-red-400 mr-2'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
              <p className='text-sm text-red-700'>{error}</p>
              <button
                onClick={() => setError(null)}
                className='ml-auto text-red-400 hover:text-red-600'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          </div>
        )
      }

      {/* Export Result Display */}
      {
        exportResult && (
          <div className='bg-green-50 border border-green-200 rounded-lg p-4 mb-6'>
            <div className='flex items-start'>
              <svg
                className='w-5 h-5 text-green-400 mr-2 mt-0.5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <div className='flex-1'>
                <h4 className='text-sm font-semibold text-green-800 mb-2'>Export Successful</h4>
                <div className='text-sm text-green-700'>
                  <pre className='bg-white border border-green-200 rounded p-3 overflow-auto max-h-96'>
                    {JSON.stringify(exportResult, null, 2)}
                  </pre>
                </div>
              </div>
              <button
                onClick={() => setExportResult(null)}
                className='ml-auto text-green-400 hover:text-green-600'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                </svg>
              </button>
            </div>
          </div>
        )
      }

      {/* Creators Table */}
      <div className='bg-white rounded-lg shadow-sm border border-gray-200'>
        <div className='px-4 md:px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3'>
          <div>
            <h3 className='text-lg font-medium text-gray-900 flex items-center gap-2'>
              Creators List
              {loading && (
                <svg
                  className='animate-spin h-4 w-4 text-blue-600'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
              )}
            </h3>
            {pagination.total > 0 && (
              <p className='text-sm text-gray-500 mt-1'>
                Showing {(pagination.page - 1) * pagination.size + 1} to{' '}
                {Math.min(pagination.page * pagination.size, pagination.total)}{' '}
                of {pagination.total} marketers
              </p>
            )}
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || loading}
            className='bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {exporting ? 'Exporting...' : 'Export Data'}
          </button>
        </div>

        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='hidden lg:table-cell px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32'>
                  ID
                </th>
                <th className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Name
                </th>
                <th className=' px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Last Name
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Status
                </th>
                <th className=' px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Join Date
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Referral Code
                </th>

                <th className=' px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Total VP
                </th>
                <th className=' px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  {/* Total Network */}Direct Partner
                </th>
                <th className=' px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Downline
                </th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  Action
                </th>
                {/* <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider'>
                  
                </th> */}
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {marketers.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan='9'
                    className='px-6 py-8 text-center text-gray-500'
                  >
                    <div className='flex flex-col items-center'>
                      <svg
                        className='w-12 h-12 text-gray-300 mb-3'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
                        />
                      </svg>
                      <p className='text-lg font-medium'>No marketers found</p>
                      <p className='text-sm'>
                        Try adjusting your search criteria or add new marketers.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                marketers.map((marketer, index) => {
                  return (
                    <tr key={index}>
                      <td className='hidden lg:table-cell px-3 py-4 text-xs text-gray-900 font-mono'>
                        <div className='' title={marketer.id}>
                          {marketer.id}
                        </div>
                      </td>
                      <td className='px-4 py-4 whitespace-nowrap'>
                        <div className='text-sm font-medium text-gray-900'>
                          {marketer.firstName}
                        </div>
                        {/* <div className='md:hidden text-xs text-gray-500'>
                            {marketer.lastName} • {marketer.joinDate}
                          </div> */}
                      </td>
                      <td className=' px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900'>
                        {marketer.lastName}
                      </td>
                      <td className='px-3 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${marketer.status === 'Plus'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {marketer.status}
                        </span>
                      </td>
                      <td className=' px-3 py-4 whitespace-nowrap text-sm text-gray-900'>
                        {marketer.joinDate}
                      </td>
                      <td className='px-3 py-4 whitespace-nowrap text-xs text-gray-900 font-mono'>
                        <div className='' title={marketer.referralCode}>
                          {marketer.referralCode}
                        </div>
                      </td>

                      <td className=' px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                        {marketer.careerPoint || 0}
                      </td>
                      <td className=' px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold'>
                        {marketer.partner || 0}
                      </td>
                      <td className=' px-3 py-4 whitespace-nowrap text-sm text-gray-900 text-center'>
                        {marketer.downline}
                      </td>
                      <td className='px-3 py-4 whitespace-nowrap text-center'>
                        <button
                          onClick={() => handleViewDetails(marketer)}
                          className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                        >
                          Detail
                        </button>
                      </td>
                      {/* <td className='px-3 py-4 whitespace-nowrap text-center'>
                          <button 
                            onClick={() => handleViewDetails(marketer)}
                            className='text-blue-600 hover:text-blue-900 text-sm font-medium'
                          >
                            Marketer
                          </button>
                        </td> */}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pageCount > 1 && (
          <div className='px-4 md:px-6 py-4 border-t border-gray-200 flex items-center justify-between'>
            <div className='flex-1 flex justify-between sm:hidden'>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pageCount || loading}
                className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Next
              </button>
            </div>
            <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
              <div>
                <p className='text-sm text-gray-700'>
                  Page <span className='font-medium'>{pagination.page}</span> of{' '}
                  <span className='font-medium'>{pagination.pageCount}</span>
                </p>
              </div>
              <div>
                <nav
                  className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
                  aria-label='Pagination'
                >
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                    className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Previous</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>

                  {/* Page numbers */}
                  {Array.from(
                    { length: Math.min(5, pagination.pageCount) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.pageCount <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.pageCount - 2) {
                        pageNum = pagination.pageCount - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          disabled={loading}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === pagination.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}

                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={
                      pagination.page >= pagination.pageCount || loading
                    }
                    className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <span className='sr-only'>Next</span>
                    <svg
                      className='h-5 w-5'
                      fill='currentColor'
                      viewBox='0 0 20 20'
                    >
                      <path
                        fillRule='evenodd'
                        d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default MarketersPage;
